import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Phone,
  PhoneCall,
  Mail,
  Clock,
  Calendar,
  MessageSquare,
  AlertCircle,
  Loader2,
  Copy,
  CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  new: { label: 'New Lead', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
  contacted: { label: 'Contacted', color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
  booked: { label: 'Appointment Booked', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' },
  closed: { label: 'Closed', color: 'text-slate-400', bg: 'bg-slate-500/10', border: 'border-slate-500/30' },
  lost: { label: 'Lost', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' }
};

const inquiryConfig = {
  emergency: { label: 'Emergency', color: 'text-red-400', bg: 'bg-red-500/10', icon: AlertCircle, description: 'Urgent service request' },
  routine: { label: 'Routine', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: Clock, description: 'Standard service inquiry' },
  estimate: { label: 'Estimate Request', color: 'text-green-400', bg: 'bg-green-500/10', icon: Calendar, description: 'Quote or estimate request' }
};

export default function ClientLeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLead();
  }, [leadId]);

  const fetchLead = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/client/leads/${leadId}`, {
        withCredentials: true
      });
      setLead(response.data);
    } catch (error) {
      console.error('Error fetching lead:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/client/login');
      } else if (error.response?.status === 404) {
        toast.error('Lead not found');
        navigate('/client/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
      </div>
    );
  }

  if (!lead) return null;

  const status = statusConfig[lead.status] || statusConfig.new;
  const inquiry = inquiryConfig[lead.inquiry_type] || inquiryConfig.routine;
  const InquiryIcon = inquiry.icon;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/client/dashboard')}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
                <Phone className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-lg">Lead Details</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Lead Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 mb-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold mb-1">{lead.name}</h1>
              <div className="text-sm text-slate-500">Lead ID: {lead.lead_id}</div>
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${status.bg} border ${status.border}`}>
              <CheckCircle2 className={`w-5 h-5 ${status.color}`} />
              <span className={`font-medium ${status.color}`}>{status.label}</span>
            </div>
          </div>

          {/* Inquiry Type Badge */}
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${inquiry.bg}`}>
            <InquiryIcon className={`w-4 h-4 ${inquiry.color}`} />
            <span className={`font-medium ${inquiry.color}`}>{inquiry.label}</span>
            <span className="text-slate-500 text-sm">— {inquiry.description}</span>
          </div>
        </motion.div>

        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 mb-6"
        >
          <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <PhoneCall className="w-5 h-5 text-sky-400" />
            Contact Information
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-slate-800">
              <span className="text-slate-400">Phone</span>
              <div className="flex items-center gap-2">
                <a href={`tel:${lead.phone}`} className="font-medium text-sky-400 hover:underline">
                  {lead.phone}
                </a>
                <button
                  onClick={() => copyToClipboard(lead.phone, 'Phone')}
                  className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4 text-slate-500" />
                </button>
              </div>
            </div>
            
            {lead.email && (
              <div className="flex items-center justify-between py-3 border-b border-slate-800">
                <span className="text-slate-400">Email</span>
                <div className="flex items-center gap-2">
                  <a href={`mailto:${lead.email}`} className="font-medium text-sky-400 hover:underline">
                    {lead.email}
                  </a>
                  <button
                    onClick={() => copyToClipboard(lead.email, 'Email')}
                    className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    <Copy className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between py-3 border-b border-slate-800">
              <span className="text-slate-400">Source</span>
              <span className="font-medium capitalize">{lead.source || 'Phone'}</span>
            </div>
            
            <div className="flex items-center justify-between py-3">
              <span className="text-slate-400">Received</span>
              <span className="font-medium">{formatDate(lead.created_at)}</span>
            </div>
          </div>
        </motion.div>

        {/* Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 mb-6"
        >
          <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-sky-400" />
            Inquiry Details
          </h2>
          
          <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
            <p className="text-slate-300 whitespace-pre-wrap">{lead.message}</p>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-col sm:flex-row gap-3"
        >
          <Button
            onClick={() => window.open(`tel:${lead.phone}`, '_self')}
            className="flex-1 bg-green-500 hover:bg-green-600"
          >
            <PhoneCall className="w-4 h-4 mr-2" />
            Call {lead.name.split(' ')[0]}
          </Button>
          
          {lead.email && (
            <Button
              onClick={() => window.open(`mailto:${lead.email}`, '_self')}
              variant="outline"
              className="flex-1 border-slate-700 text-slate-300"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Email
            </Button>
          )}
        </motion.div>
      </main>
    </div>
  );
}
